import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useClientRecordFiles } from "@/lib/clientRecordQueries";
import { useWorkDocumentsList } from "@/lib/workDocumentQueries";

const STATUS_LABEL = { draft: "Draft", active: "In progress", submitted: "Submitted", archived: "Archived" };

export default function FilesVideosTab({ workspaceId, memberId }) {
  const navigate = useNavigate();
  const { data, isPending, isError } = useClientRecordFiles(workspaceId, memberId);
  const { data: docsData, isPending: docsPending, isError: docsError } = useWorkDocumentsList(workspaceId);
  const docs = docsData?.items || [];

  return (
    <div data-testid="client-360-files-tab">
      <div className="flex justify-end mb-3">
        <button
          onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/files`)}
          className="text-xs text-terracotta hover:underline"
          data-testid="client-360-files-open-full-page"
        >
          Open full Files workbench →
        </button>
      </div>

      <p className="text-xs uppercase tracking-wide text-ink-soft mb-2">Workbooks &amp; interactive blocks</p>
      {docsPending ? (
        <p className="text-sm text-ink-soft mb-4" data-testid="client-360-workbooks-loading">Loading workbooks…</p>
      ) : docsError ? (
        <p className="text-sm text-red-600 mb-4" data-testid="client-360-workbooks-error">Couldn't load workbooks.</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-ink-soft mb-4" data-testid="client-360-workbooks-empty">No workbooks or interactive blocks yet.</p>
      ) : (
        <div className="space-y-2 mb-5" data-testid="client-360-workbooks-list">
          {docs.map((d) => (
            <button
              key={d.wdoc_id}
              onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${d.wdoc_id}`)}
              className="w-full border border-ink/10 rounded-lg p-3 bg-white/50 flex items-center justify-between gap-3 text-left hover:border-terracotta transition-colors"
              data-testid={`client-360-workbook-${d.wdoc_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles size={14} className="text-terracotta shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-ink truncate">{d.title}</p>
                  <p className="text-xs text-ink-soft">{d.visibility_scope === "workspace_shared" ? "Team" : d.owner_member_name || "Private"}</p>
                </div>
              </div>
              <span className="text-xs text-ink-soft shrink-0">{STATUS_LABEL[d.status] || d.status}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs uppercase tracking-wide text-ink-soft mb-2">Files &amp; videos</p>
      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="client-360-files-loading">Loading files…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="client-360-files-error">Couldn't load files.</p>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="client-360-files-empty">No files or videos yet.</p>
      ) : (
        <div className="space-y-2" data-testid="client-360-files-list">
          {data.items.map((f) => (
            <div key={f.fas_id} className="border border-ink/10 rounded-lg p-3 bg-white/50 flex items-center justify-between gap-3" data-testid={`client-360-file-${f.fas_id}`}>
              <p className="text-sm text-ink">{f.original_filename}</p>
              <span className="text-xs text-ink-soft uppercase shrink-0">{f.kind}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
